import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Card,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import { memo, useMemo, useState } from "react"
import { amber, deepPurple, green, grey } from "@mui/material/colors"
import { AccessTime, CheckCircle, Error, ExpandMore, Report } from "@mui/icons-material"
import {
    useDeleteHelpCenterExportNodesMutation,
    useExportHelpCenterExportNodesMutation,
    useGetHelpCenterExportLocalesQuery,
    useGetHelpCenterExportNodesQuery,
    useLoadHelpCenterExportNodesFromSpreadsheetMutation,
    useUpdateHelpCenterExportNodeMutation
} from "../../core/api/help-center-export-api"

const DEFAULT_LOCALE = "en-US"

const BRAND_MAP = Object.freeze({
    "cm": "clockmaker",
    "toh": "trainofhope",
    "mc": "belka-new-games",
    "ds": "belka-new-games"
})

const COLOR_MAP = Object.freeze({
    "CATEGORY": deepPurple[50],
    "SECTION": green[50],
    "ARTICLE_NAME": amber[50],
    "ARTICLE_CONTENT": grey[50],
    "UNKNOWN": grey[100]
})

const LocaleTabs = ({ brand, value, onChange }) => {
    const { data: locales = [], isLoading, isFetching } = useGetHelpCenterExportLocalesQuery(brand)

    if (isLoading || isFetching) {
        return <CircularProgress />
    }

    return (
        <Tabs
            orientation="vertical"
            variant="scrollable"
            value={value}
            onChange={onChange}
        >
            <Tab label={DEFAULT_LOCALE} value={DEFAULT_LOCALE} />
            {locales.map(locale => (
                <Tab key={locale} label={locale} value={locale} />
            ))}
        </Tabs>
    )
}

const NodeStatusIcon = ({ node }) => {
    switch (node.status) {
        case "RUNNING":
            return <AccessTime />
        case "SUCCESS":
            return <CheckCircle />
        case "FAILED":
            return <Report />
        default:
            return null
    }
}

const Node = memo(({ node, locale, categories, sections, articleNames, onUpdate }) => {
    const getParent = () => {
        if (node.type === "SECTION") {
            return categories
        }
        if (node.type === "ARTICLE_NAME") {
            return sections
        }
        return articleNames
    }

    const getValue = (node) => DEFAULT_LOCALE === locale ? node.value : node.translations[locale]

    return (
        <Accordion sx={{ background: COLOR_MAP[node.type] }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                    direction="row"
                    sx={{
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}>
                    <Stack spacing={1} direction="row">
                        {node.type !== "ARTICLE_CONTENT" && node.children.length === 0 && (
                            <Tooltip title="Item not contains elements">
                                <Error color="error" />
                            </Tooltip>
                        )}
                        {node.type === "ARTICLE_NAME" && node.children.length > 1 && (
                            <Tooltip title="Name contains more then 1 content">
                                <Error color="error" />
                            </Tooltip>
                        )}
                        <NodeStatusIcon node={node} />
                        <Typography noWrap sx={{
                            width: "40vw"
                        }}>
                            {getValue(node)}
                        </Typography>
                    </Stack>
                    <Stack spacing={1} direction="row" sx={{
                        pr: 2
                    }}>
                        {node.type !== "CATEGORY" && (
                            <FormControl sx={{ width: "15vw" }} size="small" disabled={getParent().length === 0}>
                                <InputLabel>
                                    Parent
                                </InputLabel>
                                <Select
                                    label="Parent"
                                    value={node.parentId}
                                    onChange={e => {
                                        e.stopPropagation()
                                        onUpdate(node.id, { parentId: e.target.value })
                                    }}
                                >
                                    {getParent().map(parent => (
                                        <MenuItem key={parent.id} value={parent.id}>
                                            {getValue(parent)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl sx={{ width: "10vw" }} size="small">
                            <InputLabel>
                                Type
                            </InputLabel>
                            <Select
                                disabled={node.children.length > 0}
                                label="Type"
                                value={node.type ?? "UNKNOWN"}
                                onChange={e => {
                                    e.stopPropagation()
                                    onUpdate(node.id, { type: e.target.value })
                                }}
                            >
                                <MenuItem value="CATEGORY">Category</MenuItem>
                                <MenuItem value="SECTION">Section</MenuItem>
                                <MenuItem value="ARTICLE_NAME">Article Name</MenuItem>
                                <MenuItem value="ARTICLE_CONTENT">Article Content</MenuItem>
                                <MenuItem disabled value="UNKNOWN">Unknown</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {
                    node.type === "ARTICLE_CONTENT"
                        ? (
                            <Typography
                                sx={{ whiteSpace: "pre-line" }}
                                variant={node.type !== "ARTICLE_CONTENT" ? "body1" : "body2"}
                            >
                                {getValue(node)}
                            </Typography>
                        )
                        : (
                            node.children.map(child => (
                                <Node
                                    key={child.id}
                                    locale={locale}
                                    node={child}
                                    categories={categories}
                                    sections={sections}
                                    articleNames={articleNames}
                                    onUpdate={onUpdate}
                                />
                            ))
                        )
                }
            </AccordionDetails>
        </Accordion>
    );
})

const ArticleExporterToolbar = ({ brand, nodes }) => {
    const [spreadsheetUrl, setSpreadsheetUrl] = useState("")

    const [loadNodesFromSpreadsheet] = useLoadHelpCenterExportNodesFromSpreadsheetMutation()
    const [deleteNodes] = useDeleteHelpCenterExportNodesMutation()
    const [exportNodes] = useExportHelpCenterExportNodesMutation()

    const handleExport = () => {
        if (window.confirm(`Confirm export to ${brand}`)) {
            exportNodes(brand)
        }
    }

    return (
        <Stack
            spacing={2}
            direction="row"
            sx={{
                width: "100%",
                justifyContent: "flex-end"
            }}>
            <TextField
                sx={{ width: "25vw" }}
                size="small"
                disabled={nodes?.length > 0}
                label="Spreadsheet URL"
                value={spreadsheetUrl}
                onChange={e => setSpreadsheetUrl(e?.target?.value?.trim() ?? "")}
            />
            <Button
                size="small"
                disabled={nodes?.length > 0 || spreadsheetUrl.length === 0}
                onClick={() => loadNodesFromSpreadsheet({ brand, spreadsheetUrl })}
            >
                Load From Spreadsheet
            </Button>
            <Button
                size="small"
                disabled={nodes?.length === 0}
                onClick={() => deleteNodes(brand)}
            >
                Clear
            </Button>
            <Button
                size="small"
                disabled={nodes?.length === 0}
                onClick={handleExport}
            >
                Export to Zendesk
            </Button>
        </Stack>
    );
}

const ArticleExporter = ({ project }) => {
    const [locale, setLocale] = useState(DEFAULT_LOCALE)

    const brand = useMemo(() => BRAND_MAP[project], [project])

    const { data: nodes = [], isLoading, isFetching } = useGetHelpCenterExportNodesQuery(brand)
    const [updateNode] = useUpdateHelpCenterExportNodeMutation()

    const nodeByStatus = useMemo(() => {
        return Object.groupBy(nodes, node => node.status)
    }, [nodes])

    const categories = useMemo(() => {
        return nodes.filter(item => item.type === "CATEGORY")
    }, [nodes])

    const sections = useMemo(() => {
        return nodes.filter(item => item.type === "SECTION")
    }, [nodes])

    const articleNames = useMemo(() => {
        return nodes.filter(item => item.type === "ARTICLE_NAME")
    }, [nodes])

    const nodeTree = useMemo(() => {
        const buildTree = (parentId = null) => {
            return nodes
                .filter(node => node.parentId === parentId)
                .map(node => ({ ...node, children: buildTree(node.id) }))
        }
        return buildTree(null)
    }, [nodes])

    const handleUpdateNode = (id, change) => {
        updateNode({ brand: brand, nodeId: id, request: change })
    }

    return (
        <Card sx={{ height: "100vh", maxHeight: "calc(100vh - 128px)", padding: 2, margin: 0 }}>
            <Stack spacing={2} direction="column" sx={{
                height: "100%"
            }}>
                <ArticleExporterToolbar brand={brand} nodes={nodes} />
                <Stack spacing={1} direction="row" sx={{
                    justifyContent: "flex-start"
                }}>
                    <Chip label={`Nodes: ${nodes.length}`} />
                    <Chip label={`Pending: ${nodeByStatus["PENDING"]?.length ?? 0}`} />
                    <Chip label={`Running: ${nodeByStatus["RUNNING"]?.length ?? 0}`} color="info" />
                    <Chip label={`Success: ${nodeByStatus["SUCCESS"]?.length ?? 0}`} color="success" />
                    <Chip label={`Failed: ${nodeByStatus["FAILED"]?.length ?? 0}`} color="error" />
                </Stack>
                <Stack sx={{ height: "100%", minHeight: 0, flexGrow: 1 }} direction="row">
                    <LocaleTabs
                        brand={brand}
                        value={locale}
                        onChange={(e, next) => setLocale(next)}
                    />
                    <Stack sx={{ width: "100%", height: "100%", overflowY: "auto" }} direction="column">
                        {nodeTree.map(node => (
                            <Node
                                key={node.id}
                                node={node}
                                locale={locale}
                                categories={categories}
                                sections={sections}
                                articleNames={articleNames}
                                onUpdate={handleUpdateNode}
                            />
                        ))}
                    </Stack>
                </Stack>
            </Stack>
        </Card>
    );
}

export default ArticleExporter
